import * as Sentry from '@sentry/react';

// Initialize Sentry for React frontend
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
  const release = import.meta.env.VITE_SENTRY_RELEASE || '1.0.0';

  if (!dsn) {
    console.warn('Sentry DSN not found. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Performance monitoring - using built-in browser tracing
    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Source map configuration
    attachStacktrace: true,
    
    // Release tracking for source maps
    dist: process.env.NODE_ENV === 'production' ? 'production' : 'development',
    
    // Capture errors and logs
    beforeSend(event, hint) {
      // Add custom context
      event.tags = {
        ...event.tags,
        frontend: true,
        app_version: release,
        user_agent: navigator.userAgent,
      };

      // Add user context if available
      const user = getUserFromStorage();
      if (user) {
        event.user = {
          id: user.id,
          email: user.email,
          username: user.username,
        };
      }

      // Filter out certain errors if needed
      if (event.exception) {
        const error = hint.originalException as Error;
        if (error?.message?.includes('ResizeObserver loop limit exceeded')) {
          return null; // Ignore this common error
        }
      }

      return event;
    },

    // Enable debug mode in development
    debug: environment === 'development',
    
    // Send default PII (Personal Identifiable Information)
    sendDefaultPii: false,

  });

  console.log(`🚀 Sentry initialized for ${environment} environment`);
}

// Helper function to get user from storage
function getUserFromStorage() {
  try {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

// Enhanced error boundary component
export function withSentryErrorBoundary<P extends object>(
  Component: React.ComponentType<P>
) {
  return Sentry.withErrorBoundary(Component, {
    beforeCapture: (scope) => {
      scope.setTag('component', Component.name);
      scope.setContext('component', {
        name: Component.name,
        props: Component.displayName,
      });
    },
  });
}

// Export Sentry for manual usage
export { Sentry };
